import {
  Button, Collapse, Divider, Grid, IconButton, Slide
} from '@material-ui/core';
import { FilterValue } from 'api/query/shared/filter-value';
import Dropdown from 'components/dropdown';
import { DropdownItem } from 'components/dropdown/dropdown-item';
import { ChevronRightOutlinedIcon } from 'components/icons/chevron-right-outlined';
import { InfoIcon } from 'components/icons/info';
import { InfoOutlinedIcon } from 'components/icons/info-outlined';
import { RelativeTooltip } from 'components/relative-tooltip/relative-tooltip';
import { RelativeTooltipContent } from 'components/relative-tooltip/relative-tooltip-content';
import { useResults } from 'pages/shared/use-results';
import React, {
  memo, useCallback, useEffect, useState
} from 'react';
import { DisplayFormat } from 'shared/display-format.enum';
import { addToStorage, getFromStorage, StorageKey } from 'utils/storage-utils';
import { useBreakpoints } from 'utils/use-breakpoints';
import { CustomiseASBVsButton } from '../customise-asbvs/customise-asbvs-button';
import { bottomPercentileLabels, dataViewLabels, topPercentileLabels } from '../results-utils';
import { SortDir } from '../sort-dir.enum';
import { DisplayOption } from './display-option.enum';
import { DisplayOptionsProps } from './display-options-props';
import { findSortingOption, getDisplayOptionsTooltipContent } from './display-options-utils';
import './display-options.scss';

export const DisplayOptions: React.FC<DisplayOptionsProps> = memo((props) => {
  const { displayDataOptions = true, disablePercentiles = false, sortingOptions } = props;
  const cachedSelection = getFromStorage<FilterValue>(StorageKey.Display);
  const cachedSelectionItem = { id: cachedSelection?.id, value: cachedSelection?.value };
  const topPercentileSelection = getFromStorage<FilterValue>(StorageKey.TopPercentile);
  const topPercentileSelectionItem = { id: topPercentileSelection?.id, value: topPercentileSelection?.value };
  const bottomPercentileSelection = getFromStorage<FilterValue>(StorageKey.BottomPercentile);
  const bottomPercentileSelectionItem = { id: bottomPercentileSelection?.id, value: bottomPercentileSelection?.value };
  const { phone } = useBreakpoints();
  const {
    setTopPercentile, setBottomPercentile, display, setDisplay, sortByASBV, setSortByASBV,
  } = useResults();
  const [selection, setSelection] = useState<DropdownItem>();
  const [show, setShow] = useState<boolean>(false);
  const [options, setOptions] = useState(dataViewLabels);
  const [actionProps, setActionProps] = useState<{ label: string, variant: 'outlined' | 'contained' }>({ variant: 'outlined', label: 'Close' });
  const [tooltipContent, setTooltipContent] = useState<RelativeTooltipContent>();
  const [showTooltip, setShowTooltip] = useState<DisplayOption | null>(null);
  // keeps sorting dropdown selection
  const [sortBy, setSortBy] = useState<string>(sortByASBV.sortBy);
  const [defaultSort, setDefaultSort] = useState(findSortingOption(sortingOptions, sortByASBV.sortBy));

  // Object used to set initial default selection and pass it to parent component.
  // If there is a cached value it will be populated
  // other way first option from dropdown will be the defualt (Data view).
  const defaultSelection: DropdownItem = ((cachedSelectionItem.value === DisplayFormat.Graph && phone) || !cachedSelectionItem.value) ? options[0] : cachedSelectionItem;
  const defaultTopPercentile: DropdownItem = !topPercentileSelectionItem.value ? topPercentileLabels[2] : topPercentileSelectionItem;
  const defaultBottomPercentile: DropdownItem = !bottomPercentileSelectionItem.value ? bottomPercentileLabels[0] : bottomPercentileSelectionItem;

  /**
   * Removes graph view from options on mobile and
   * rolls back selection to data view if graph view was selected.
   */
  const removeGraphOption = useCallback(() => {
    options.forEach((el, i) => {
      if (el.value === DisplayFormat.Graph) {
        options.splice(i, 1);

        // set selected value to data view if cached or selected value was graph view
        if (cachedSelectionItem.value === el.value || selection?.value === el.value) {
          setSelection(options[0]);
          setDisplay(options[0].value);
        }
      }
    });
  }, [options, cachedSelectionItem.value, selection, setDisplay]);

  const passNewDisplay = (value: any) => {
    setDisplay(value);
  };

  /**
   * Passes new selection to parent component on desktop
   * and saves selected value to local storage.
   */
  const onSelection = (selected: DropdownItem) => {
    setSelection(selected);
    if (selected !== defaultSelection) {
      addToStorage(StorageKey.Display, selected);

      if (!phone) {
        passNewDisplay(selected.value);
      }
    }

    setActionProps({ variant: 'contained', label: 'Apply' });
  };

  /**
   * Passes new selection to parent component on desktop
   * and saves selected value to local storage.
   */
  const onPercentileSelection = (selected: DropdownItem, isTop: boolean) => {
    if (isTop) {
      if (selected !== defaultTopPercentile) {
        addToStorage(StorageKey.TopPercentile, selected);
        setTopPercentile(selected?.id as number);
      }
    } else if (!isTop && selected !== defaultBottomPercentile) {
      addToStorage(StorageKey.BottomPercentile, selected);
      setBottomPercentile(selected?.id as number);
    }
    setActionProps({ variant: 'contained', label: 'Apply' });
  };

  /**
   * Sets local state for sort selection.
   * @param selected selected asbv to sort results
   */
  const onSortChange = (selected: DropdownItem) => {
    if (selected.id !== sortBy) {
      setSortBy(selected.id as string);
      setDefaultSort({ id: selected.id as string, value: selected.value });
    }

    setActionProps({ variant: 'contained', label: 'Apply' });
  };

  /**
   * Passes new selection to parent component on mobile.
   */
  const handleApply = () => {
    setShow(false);
    setActionProps({ variant: 'outlined', label: 'Close' });
    if (selection) {
      setTimeout(() => {
        setDisplay(selection.value);
        passNewDisplay(selection.value);
      }, phone ? 125 : 0);
    }
    // saves new sort selection to results state
    if (sortBy !== '' && sortBy !== sortByASBV.sortBy) {
      setSortByASBV({ sortBy, sortDir: SortDir.BestToWorst });
    }
  };

  /**
   * Shows/hides display options on mobile.
   */
  const toggleShow = () => (show ? setShow(false) : setShow(true));

  /**
   * Toggles tooltip visibility and sets the tooltip content.
   * @param type tooltip to open
   */
  const toggleTooltip = (type: DisplayOption | null) => {
    const content = getDisplayOptionsTooltipContent(type as DisplayOption);
    setTooltipContent(content);
    setShowTooltip(showTooltip === type ? null : type);
  };

  /**
   * Creates tooltip button.
   * @param trigger
   */
  const createTooltipButton = (trigger: DisplayOption) => (
    <IconButton
      className={`sg-relative-tooltip__button ${showTooltip === trigger ? 'active' : ''}`}
      size="small"
      aria-label="info"
      onClick={() => toggleTooltip(trigger)}
      disabled={disablePercentiles && trigger === DisplayOption.PercentileHighlights}
    >
      {showTooltip !== trigger && (<InfoOutlinedIcon />)}
      {showTooltip === trigger && (<InfoIcon />)}
    </IconButton>
  );

  /**
   * Closes the modal (phone) break point.
   */
  const closeModal = () => {
    if (phone) {
      setShow(false);
    }
  };

  useEffect(() => {
    setSortBy(sortByASBV.sortBy);
    setDefaultSort(findSortingOption(sortingOptions, sortByASBV.sortBy));
  }, [sortingOptions, sortByASBV.sortBy]);

  /**
   * Set options depend on display size.
   */
  useEffect(() => {
    if (phone) {
      removeGraphOption();
    } else {
      setOptions(dataViewLabels);
    }
  }, [phone, removeGraphOption]);

  /**
   * Hide display options on mobile by default.
   */
  useEffect(() => {
    if (phone) {
      setShow(false);
    } else {
      setShow(true);
    }
  }, [phone]);

  return (
    <div className="sg-display-options">
      <Collapse
        in={phone ? true : !!showTooltip}
        mountOnEnter
        unmountOnExit
      >
        <RelativeTooltip
          content={tooltipContent}
          onClose={() => toggleTooltip(null)}
        />
      </Collapse>
      {phone && (
        <>
          <Button
            variant="text"
            onClick={toggleShow}
            className="sg-display-options__button"
          >
            <h3>Display Options</h3>
            <ChevronRightOutlinedIcon />
          </Button>
        </>
      )}
      <Slide
        direction="left"
        in={show}
        timeout={phone ? 150 : 0}
      >
        <Grid
          item
          xs={12}
          className="sg-display-options__container  container-padding"
        >
          <Grid
            container
            item
            xs={12}
            justify="space-between"
            className="sg-display-options__wrapper"
          >
            <Grid
              item
              md={6}
            >
              {phone && <h3>Display options</h3>}
              <div className="sg-display-options__data">
                {displayDataOptions && (
                  <>
                    <p className="sg-display-options__title">Data <br />display
                      {createTooltipButton(DisplayOption.DataDisplay)}
                    </p>
                    <div>
                      <Dropdown
                        items={options}
                        item={defaultSelection}
                        defaultValue="Data view"
                        onSelectionChange={(el) => onSelection(el[0])}
                        disabled={phone}
                      />
                    </div>
                  </>
                )}
                <CustomiseASBVsButton
                  disabled={display === DisplayFormat.Group}
                  onSave={() => closeModal()}
                />
              </div>
            </Grid>
            {phone && <Divider variant="fullWidth" />}
            <Grid
              item
              md={6}
            >
              <div className="sg-display-options__percentiles">
                <p className="sg-display-options__title">Percentile <br />highlights
                  {createTooltipButton(DisplayOption.PercentileHighlights)}
                </p>
                <Dropdown
                  items={topPercentileLabels}
                  item={!disablePercentiles ? defaultTopPercentile : undefined}
                  disabled={disablePercentiles}
                  className="sg-display-options__percentile"
                  placeholder="TOP All"
                  onSelectionChange={(el) => onPercentileSelection(el[0], true)}
                />
                <Dropdown
                  items={bottomPercentileLabels}
                  item={!disablePercentiles ? defaultBottomPercentile : undefined}
                  disabled={disablePercentiles}
                  className="sg-display-options__percentile"
                  placeholder="Bottom All"
                  onSelectionChange={(el) => onPercentileSelection(el[0], false)}
                />
              </div>
            </Grid>
            {phone && (
              <>
                <Divider variant="fullWidth" />
                <Grid
                  item
                  md={6}
                >
                  <div className="sg-display-options__percentiles">
                    <p className="sg-display-options__title">Sort by
                    </p>
                    <Dropdown
                      items={sortingOptions}
                      item={defaultSort}
                      className="sg-display-options__percentile"
                      onSelectionChange={(el) => onSortChange(el[0])}
                    />
                  </div>
                </Grid>
              </>
            )}
          </Grid>
          {/* Apply button on mobile view */}
          {phone && (
            <div className="sg-display-options__action">
              <Divider variant="fullWidth" />
              <div>
                <Button
                  fullWidth
                  variant={actionProps.variant}
                  onClick={handleApply}
                >
                  {actionProps.label}
                </Button>
              </div>
            </div>
          )}
        </Grid>
      </Slide>
    </div>
  );
});

// (DisplayOptions as any).whyDidYouRender = true;
